/**
 * 접힌 사이드바 아이콘의 즉시 툴팁과 모바일 탐색.
 *
 * 네이티브 title은 브라우저가 1초 넘게 지연시키고, 사이드바는 스크롤
 * 컨테이너라 CSS ::after 툴팁은 가장자리에서 잘린다. 그래서 hover 즉시
 * body에 fixed 툴팁을 붙인다. 레일 폭에서만 동작하고, 그 밖의 폭에서는
 * 네이티브 title을 그대로 둔다 (전체 폭에선 라벨이 보이므로 쓸 일도 없다).
 *
 * SPA (2026-09-10): 관리자 화면의 제자리 교체는 사이드바 종류가 바뀔 때
 * #adminSidebar 를 통째로 갈아끼운다(calendar-history.js syncSidebar). 그래서
 * 요소에 붙는 리스너만 요소별 가드(data-rail-ready)로 다시 붙이고, 문서·창
 * 리스너는 모듈에 한 번만 붙인 뒤 발생 시점에 살아 있는 #adminSidebar 를 다시
 * 찾는다 — 예전에는 교체 때마다 click·keydown·resize 가 쌓여 detached 사이드바를
 * 붙든 채 남았다.
 */
import { openExclusive, closedExclusive } from './popover-registry.js';
import { isPhone } from './phone.js';
import { closeOnBack } from './close-on-back.js';

let tip = null;

function showTip(target, text) {
    hideTip();
    tip = document.createElement('div');
    tip.className = 'rail-tip';
    tip.textContent = text;
    document.body.appendChild(tip);
    const r = target.getBoundingClientRect();
    tip.style.top = Math.round(r.top + r.height / 2) + 'px';
    tip.style.left = Math.round(r.right + 8) + 'px';
    // 다음 프레임에 표시 클래스 → 아주 짧은 페이드만 타고 즉시 등장
    requestAnimationFrame(() => tip && tip.classList.add('show'));
}

function hideTip() {
    if (tip) {
        tip.remove();
        tip = null;
    }
}

// 항목의 툴팁 문구: data-tip > title > 라벨 텍스트(배지 숫자 제외).
// title 없는 사이드바(관리자 등)도 라벨에서 자동으로 얻는다 — 레일 폭에서
// 라벨은 font-size:0 으로 숨겨질 뿐 텍스트는 그대로 있다.
function tipText(item) {
    if (item.dataset.tip) return item.dataset.tip;
    if (item.hasAttribute('title')) {
        // 네이티브 지연 툴팁과 중복되지 않게 title → data-tip으로 옮긴다.
        item.dataset.tip = item.getAttribute('title');
        item.removeAttribute('title');
        return item.dataset.tip;
    }
    const clone = item.cloneNode(true);
    clone.querySelectorAll('.item-count, .nav-preview').forEach((b) => b.remove());
    const text = clone.textContent.replace(/\s+/g, ' ').trim();
    if (text) item.dataset.tip = text;
    return text;
}

// ── 살아 있는 요소를 발생 시점에 찾는다 (사이드바는 교체될 수 있다) ──────────
const AUTO_RAIL_MAX = 1100;
const liveSidebar = () => document.getElementById('adminSidebar');
const mobile = () => isPhone();
// 앱 셸의 사이드바는 apps.js 가 따로 다룬다 — 드로어 동작을 붙이지 않는다.
const drawerSidebar = () => {
    const sidebar = liveSidebar();
    return sidebar && !sidebar.closest('.apps-shell') ? sidebar : null;
};
// toggleAdminSidebar() 를 부르는 요소 전부 (버튼 + 배경 오버레이 div).
// 팝오버 등록부의 "바깥 클릭" 판정에서는 이 전부를 안쪽으로 친다: 배경을 누르면
// 그 자리의 onclick 이 토글로 닫는데, 캡처 단계의 등록부가 먼저 닫아 버리면
// 이어서 도는 onclick 이 도로 열어 버린다 (모바일 드로어가 안 닫히던 회귀).
const drawerControls = () => [...document.querySelectorAll('[onclick="toggleAdminSidebar()"]')];
// aria-expanded·햄버거 상태를 갱신할 대상은 버튼뿐이다.
const drawerTriggers = () => drawerControls().filter((el) => el.tagName === 'BUTTON');

// ── 레일 펼침 상태 (localStorage 로 페이지 간 유지) ──────────────────────────
// 키는 사이드바 종류마다 다르다 (spec §3.7.1): 전역 사이드바는 navRailOpen,
// /admin/* 전용 관리자 사이드바만 옛 adminRailOpen 을 계속 쓴다. 예전에 관리
// 화면 레일을 접어 둔 사용자가 그 상태를 **모든** 화면에 끌고 다니던 회귀가
// 이것 때문이었다. 값은 모듈 로드가 아니라 initRailTooltips() 에서 읽는다 —
// 로드 시점에는 어느 사이드바가 있는지 알 수 없고, 사이드바가 통째로 갈리면
// (data-sidebar 불일치 교체) 키가 바뀌기 때문이다.
const RAIL_KEY_GLOBAL = 'navRailOpen';
const RAIL_KEY_ADMIN = 'adminRailOpen';
let railKey = RAIL_KEY_GLOBAL;
let saved = null;

function readRailState() {
    const sidebar = liveSidebar();
    railKey = sidebar && sidebar.dataset.sidebar === 'admin' ? RAIL_KEY_ADMIN : RAIL_KEY_GLOBAL;
    try { saved = localStorage.getItem(railKey); } catch (err) { saved = null; }
}

const railOpen = () => (saved === '1' ? true : (saved === '0' ? false : window.innerWidth > AUTO_RAIL_MAX));

function paintRail() {
    const sidebar = liveSidebar();
    const root = document.documentElement;
    root.classList.toggle('rail-open', saved === '1');
    root.classList.toggle('rail-closed', saved === '0');
    if (!sidebar) return;
    const open = railOpen();
    sidebar.classList.toggle('rail-open', open);
    const toggle = sidebar.querySelector('.rail-expand-btn');
    if (toggle) {
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        // 접근 이름은 버튼이 하는 일을 말해야 한다 (L4): 펼쳐져 있으면 "접기",
        // 접혀 있으면 "펼치기". 두 문구는 서버가 data-label-* 로 실어 보낸다
        // (번역본이므로 여기서 하드코딩하지 않는다).
        const label = open
            ? (toggle.dataset.labelCollapse || '')
            : (toggle.dataset.labelExpand || '');
        if (label) {
            toggle.setAttribute('aria-label', label);
            toggle.setAttribute('title', label);
        }
    }
}

// 전역 사이드바(modules/global-sidebar.js)가 레일 폭에서 그룹 아이콘을 눌렀을
// 때 레일을 펼치려고 부른다. 상태 저장은 이 모듈 한 곳에만 둔다.
export function setRailOpen(open) {
    saved = open ? '1' : '0';
    try { localStorage.setItem(railKey, saved); } catch (err) { /* 무시 */ }
    paintRail();
}

// 레일 폭(아이콘만 보이는 상태)인지 — 아코디언이 클릭 처리 방식을 가른다.
export function isRailNarrow() {
    const sidebar = liveSidebar();
    return !!sidebar && !isPhone() && sidebar.getBoundingClientRect().width <= 100;
}

// ── 모바일 드로어 ───────────────────────────────────────────────────────────
let returnFocus = null;
let drawerBack = null; // 열린 드로어를 안드로이드의 뒤로 가기가 닫는다 (close-on-back.js, 2026-10-02)

function releaseDrawerBack() {
    if (drawerBack) drawerBack.release();
    drawerBack = null;
}

function setDrawer(open, restoreFocus = false) {
    const sidebar = drawerSidebar();
    if (!sidebar) { releaseDrawerBack(); return; }
    const triggers = drawerTriggers();
    // 드로어도 팝오버다: 다른 메뉴를 열면 닫히고, 바깥 클릭·Esc 는 공용 등록부가
    // 처리한다 (모바일 폭에서만 — 넓은 화면에서는 드로어를 쓰지 않는다).
    if (open && mobile()) {
        openExclusive('sidebar-drawer', () => setDrawer(false, true), { roots: () => [drawerSidebar(), ...drawerControls()].filter(Boolean) });
        if (!drawerBack) drawerBack = closeOnBack(() => setDrawer(false, true), () => !!drawerSidebar()?.classList.contains('mobile-open'));
    } else {
        closedExclusive('sidebar-drawer');
        releaseDrawerBack();
    }
    sidebar.classList.toggle('mobile-open', open);
    sidebar.inert = mobile() && !open;
    // 폰에서 드로어가 열린 동안 뒤 화면은 스크롤되지 않는다 (spec 2026-09-25-phone-landscape §E.2-2)
    document.documentElement.classList.toggle('m-drawer-open', open && mobile());
    for (const button of triggers) {
        button.setAttribute('aria-controls', 'adminSidebar');
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (button.classList.contains('hamburger-menu')) button.classList.toggle('active', open);
    }
    if (open) {
        returnFocus = document.activeElement;
        const destination = sidebar.querySelector('a.active') || sidebar.querySelector('a');
        destination?.focus({ preventScroll: true });
        destination?.scrollIntoView({ block: 'nearest' });
    } else if (restoreFocus) {
        // 가장자리 스와이프로 열었으면 열기 전 포커스가 body 다 — 그때는 ☰ 로 (inert 가 된 링크에 남지 않게)
        const back = returnFocus?.isConnected && returnFocus !== document.body
            ? returnFocus
            : triggers.find((b) => b.classList.contains('hamburger-menu') && b.getClientRects().length);
        back?.focus();
    }
    hideTip();
}

// 가장자리 스와이프(touch-gestures.js)가 연다 — ☰ 과 같은 길(포커스·inert·aria)이다.
export function openAdminDrawer() {
    if (drawerSidebar() && !drawerSidebar().classList.contains('mobile-open')) setDrawer(true);
}

// 제자리 교체 뒤 main.js 가 부른다: 사이드바가 통째로 갈렸어도 살아 있는 것을 닫는다.
// 밀어 닫기(touch-gestures.js)는 restoreFocus 로 ☰·배경과 같은 포커스 규칙을 탄다.
export function closeAdminDrawer({ restoreFocus = false } = {}) {
    const sidebar = drawerSidebar();
    if (!sidebar) { closedExclusive('sidebar-drawer'); releaseDrawerBack(); return; }
    setDrawer(false, restoreFocus);
}

// ── 문서·창 리스너: 모듈에 한 번만 (교체마다 쌓이지 않게) ───────────────────
let globalsWired = false;
function wireGlobals() {
    if (globalsWired) return;
    globalsWired = true;

    document.addEventListener('click', hideTip);
    window.addEventListener('resize', () => {
        paintRail();
        const sidebar = drawerSidebar();
        if (!sidebar) return;
        if (!mobile()) setDrawer(false);
        else sidebar.inert = !sidebar.classList.contains('mobile-open');
    }, { passive: true });
    // 바깥 클릭과 Esc 는 팝오버 등록부가 처리한다 (setDrawer 참조) — 여기 남는 건 Tab 가둠뿐.
    document.addEventListener('keydown', (event) => {
        const sidebar = drawerSidebar();
        if (!sidebar || !mobile() || !sidebar.classList.contains('mobile-open')) return;
        if (event.key !== 'Tab') return;
        const links = [...sidebar.querySelectorAll('a[href], button')].filter((el) => el.getClientRects().length);
        const first = links[0], last = links.at(-1);
        if (event.shiftKey && document.activeElement === first) { last?.focus(); event.preventDefault(); }
        else if (!event.shiftKey && document.activeElement === last) { first?.focus(); event.preventDefault(); }
    });
}

export function initRailTooltips() {
    const sidebar = document.getElementById('adminSidebar');
    if (!sidebar || sidebar.dataset.railReady) return;
    sidebar.dataset.railReady = '1';
    wireGlobals();

    const offerTip = (e) => {
        if (isPhone() || sidebar.getBoundingClientRect().width > 100) return;
        // .gnav-head 는 레일에서 항목 목록이 숨으므로 그룹 이름을 알 유일한
        // 수단이다 (§3.7.1) — title 이 없어도 tipText() 가 라벨 텍스트에서 뽑는다.
        const item = e.target.closest('.admin-sidebar-item, .gnav-head, [title], [data-tip]');
        if (!item || !sidebar.contains(item)) return;
        const text = tipText(item);
        if (text) showTip(item, text);
    };
    sidebar.addEventListener('mouseover', offerTip);
    sidebar.addEventListener('focusin', offerTip);
    sidebar.addEventListener('mouseout', () => hideTip());
    sidebar.addEventListener('focusout', hideTip);
    sidebar.addEventListener('scroll', hideTip, { passive: true });

    // ── 레일 펼침 토글: 아이콘만으론 구분이 어려울 때 라벨까지 펼쳐 고정한다. ──
    // 첫 페인트 전에 <head> 의 인라인 스크립트가 저장된 선택을 html.rail-open /
    // html.rail-closed 로 이미 붙여 둔다. 선택이 없으면 폭에 따른 자동(≤1100px 레일).
    const toggle = sidebar.querySelector('.rail-expand-btn');
    readRailState(); // 이 사이드바가 쓰는 키로 상태를 다시 읽는다 (전역 vs 관리자)
    paintRail();
    if (toggle) {
        toggle.addEventListener('click', () => {
            setRailOpen(!railOpen());
            hideTip();
        });
    }

    if (!sidebar.closest('.apps-shell')) {
        window.toggleAdminSidebar = () => {
            const live = drawerSidebar();
            setDrawer(!(live && live.classList.contains('mobile-open')), true);
        };
        setDrawer(false);
    }
}
